import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { getEmailValidation, registerUser } from "../../Store/Action";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { CSSTransition } from "react-transition-group"; // Import CSSTransition
import {useThemeClass} from '@/Hooks/useThemeClass'
import clsx from 'clsx';

const steps = [
  "email",
  "name",
  "nick_name",
  "password_confirm",
  "phone",
  "blog_url",
  "terms",
];

const NewMainRegister = () => {
  const themeClass = useThemeClass()
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [inProp, setInProp] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    nick_name: "",
    password: "",
    confirm_password: "",
    username: "",
    phone: "",
    blog_url: "",
    terms_accepted: false,
    privacy_accepted: false,
    marketing_accepted: false,
  });
  const [blogUrlError, setBlogUrlError] = useState("");

  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [direction, setDirection] = useState("next");

  const inputRef = useRef(null);

  const handleNext = () => {
    if (currentStep === steps.indexOf("blog_url")) {
      const isValid = validateBlogUrl(formData.blog_url);
      if (!isValid) {
        alert("유효한 SNS/블로그 URL을 입력해주세요");
        return;
      }
    }

    const validations = [
      {
        condition: !formData.email || emailValidationError,
        message: "이메일은 이미 사용 중입니다.",
      },
      { condition: !formData.name, message: "값을 입력해주세요." },
      { condition: !formData.nick_name, message: "값을 입력해주세요." },
      {
        condition: !formData.password || !formData.confirm_password,
        message: "값을 입력해주세요.",
      },
      {
        condition: formData.password !== formData.confirm_password,
        message: "비밀번호가 일치하지 않습니다.",
      },
      { condition: !formData.phone, message: "값을 입력해주세요." },
      { condition: !formData.blog_url, message: "값을 입력해주세요." },
      {
        condition: !formData.terms_accepted || !formData.privacy_accepted,
        message: "약관에 동의해주세요.",
      },
    ];

    if (validations[currentStep]?.condition) {
      alert(validations[currentStep].message);
      return;
    }

    if (currentStep < steps.length - 1) {
      setDirection("next");
      setInProp(false);
      setTimeout(() => {
        setCurrentStep((prev) => prev + 1);
        setInProp(true);
      }, 300);
    }
  };

  // Menangani Enter
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Mencegah submit form default
      handleNext();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setDirection("back");
      setInProp(false);
      setTimeout(() => {
        setCurrentStep((prev) => prev - 1);
        setInProp(true);
      }, 300);
    }
  };

  const [passwordError, setPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [emailValidationError, setEmailValidationError] = useState("");
  const [isEmailValidating, setIsEmailValidating] = useState(false);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "email") {
      setFormData((prev) => ({
        ...prev,
        email: value,
        username: value,
      }));
      // validateEmail(value);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
    if (name === "blog_url") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      // Validate only when user finishes typing (onBlur)
    }
    if (name === "password" || name === "confirm_password") {
      const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]{8,10}$/;
      if (name === "password") {
        setPasswordError(!passwordRegex.test(value));
      }

      if (name === "confirm_password" && value !== formData.password) {
        setConfirmPasswordError(true);
      } else {
        setConfirmPasswordError(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      //   console.log(formData);
      const response = await dispatch(registerUser(formData));

      if (response?.meta?.code === 409) {
        alert("이미 존재하는 사용자입니다. (User already exists)");
        return;
      }

      setRegistrationSuccess(true);
    } catch (error) {
      console.error("Registration error:", error);
      alert(
        "회원가입에 실패했습니다. 다시 시도해주세요. (Registration failed)"
      );
    }
  };

  const [isChecked, setIsChecked] = useState({
    option1: false,
    option2: false,
    option3: false,
    option4: false,
  });

  const handleCheck = (e) => {
    const { name, checked } = e.target;

    if (name === "option1") {
      const newState = {
        option1: checked,
        option2: checked,
        option3: checked,
        option4: checked,
      };

      setIsChecked(newState);
      setFormData((prev) => ({
        ...prev,
        terms_accepted: checked,
        privacy_accepted: checked,
        marketing_accepted: checked,
      }));
    } else {
      setIsChecked((prevState) => {
        const updatedState = {
          ...prevState,
          [name]: checked,
        };

        const allChecked =
          updatedState.option2 && updatedState.option3 && updatedState.option4;
        updatedState.option1 = allChecked;

        setFormData((prev) => ({
          ...prev,
          terms_accepted: updatedState.option2,
          privacy_accepted: updatedState.option3,
          marketing_accepted: updatedState.option4,
        }));

        return updatedState;
      });
    }
  };

  const validateEmail = async (email) => {
    if (!email) return; // Jangan validasi jika input kosong

    setIsEmailValidating(true);
    try {
      const response = await dispatch(getEmailValidation(email));
      // console.log(response);

      if (
        response?.meta?.code === 400 ||
        response?.meta?.message?.toLowerCase().includes("email already exists")
      ) {
        setEmailValidationError("이메일은 이미 사용 중입니다."); // Email sudah digunakan
      } else if (response?.meta?.code === 200) {
        setEmailValidationError(""); // Email tersedia
      } else {
        setEmailValidationError("이메일 확인 중 오류가 발생했습니다."); // Kesalahan umum
      }
    } catch (error) {
      // console.error("Email validation error:", error);
      if (error.message.toLowerCase().includes("email already exists")) {
        setEmailValidationError("이메일은 이미 사용 중입니다."); // Pastikan error 400 ditangani
      } else {
        setEmailValidationError("이메일 확인 중 오류가 발생했습니다."); // Kesalahan umum
      }
    } finally {
      setIsEmailValidating(false);
    }
  };

  // Validate URL
  const validateBlogUrl = (url) => {
    if (!url) {
      setBlogUrlError("URL을 입력해주세요");
      return false;
    }
  
    let trimmedUrl = url.trim().replace(/\/+$/, '');
  
    // Tambahkan protokol jika belum ada
    if (!/^https?:\/\//i.test(trimmedUrl)) {
      trimmedUrl = "https://" + trimmedUrl;
    }
  
    const naverPatterns = [
      /^(https?:\/\/(?:m\.)?blog\.naver\.com\/[a-zA-Z0-9_-]+)$/i,
      /^(https?:\/\/(?:m\.)?blog\.naver\.com\/[a-zA-Z0-9_-]+\/post\/\d+)$/i,
      /^(https?:\/\/(?:m\.)?blog\.naver\.com\/PostView\.nhn\?blogId=[a-zA-Z0-9_-]+(&logNo=\d+)?)$/i,
    ];
  
    const isValid = naverPatterns.some((pattern) => pattern.test(trimmedUrl));
    if (!isValid) {
      setBlogUrlError("유효한 네이버 블로그 URL이 아닙니다");
      return false;
    }
  
    setFormData((prev) => ({ ...prev, blog_url: trimmedUrl }));
  
    setBlogUrlError("");
    return true;
  };
  

  return (
    <div className="flex flex-col items-center justify-center py-6 min-h-[150vw] lg:min-h-screen">
      {registrationSuccess ? (
        <div className="text-center">
          <h2 className={clsx(themeClass.text('text'), 'text-2xl font-bold mb-2')}>
            회원가입 완료!
          </h2>
          <p className="text-gray-400 text-xs">
            위유 멤버가 되신 것을 환영합니다 :)
          </p>
          <button
            onClick={() => navigate("/login")}
            className={clsx(themeClass.bg('text'), 'text-white px-6 py-2 w-full rounded-full mt-8')}
          >
            로그인 페이지로 가기
          </button>
        </div>
      ) : (
        <>
          <div className="w-8/12 md:w-6/12 lg:w-4/12 relative overflow-hidden">
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-3"
              onKeyDown={handleKeyDown}
            >
              {steps.map((step, index) => {
                return (
                  <CSSTransition
                    key={step}
                    in={inProp && currentStep === index}
                    timeout={300}
                    classNames={`input-slide-${direction}`}
                    unmountOnExit
                  >
                    <div className="w-full">
                      {step === "email" && (
                        <div className="flex text-center flex-col gap-9 text-lg">
                          <p className="font-bold">이메일을 입력해주세요.</p>
                          <input
                            ref={inputRef}
                            type="text"
                            name="email"
                            className={clsx(`bg-[#F5F5F5] text-center text-[#D9D9D9] w-full border px-4 py-3 rounded-full focus:outline-none focus:ring-0 focus:border-transparent focus:${themeClass.bg('background')} focus:text-[#0F1E6C] border-[#E8EAF9]`)}
                            placeholder="이메일"
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={(e) => validateEmail(e.target.value)} // 🔹 Validasi saat input kehilangan fokus
                            required
                          />
                          {isEmailValidating && (
                            <p className={clsx(themeClass.text('text'), `text-xs`)}>
                              이메일 확인 중...
                            </p>
                          )}{" "}
                          {emailValidationError && (
                            <p className="text-red-500 text-xs ">
                              {emailValidationError}
                            </p>
                          )}
                        </div>
                      )}
                      {step === "name" && (
                        <div className="flex text-center flex-col gap-9 text-lg">
                          <p className="font-bold">이름을 입력해주세요.</p>
                          <input
                            ref={inputRef}
                            name="name"
                            className={clsx(`bg-[#F5F5F5] text-center text-[#D9D9D9] w-full border px-4 py-3 rounded-full focus:outline-none focus:ring-0 focus:border-transparent focus:${themeClass.bg('background')} focus:text-[#0F1E6C] border-[#E8EAF9]`)}
                            placeholder="이름"
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      )}
                      {step === "nick_name" && (
                        <div className="flex text-center flex-col gap-9 text-lg">
                          <p className="font-bold">닉네임을 지정해주세요.</p>
                          <input
                            ref={inputRef}
                            name="nick_name"
                            className={clsx(`bg-[#F5F5F5] text-center text-[#D9D9D9] w-full border px-4 py-3 rounded-full focus:outline-none focus:ring-0 focus:border-transparent focus:${themeClass.bg('background')} focus:text-[#0F1E6C] border-[#E8EAF9]`)}
                            placeholder="닉네임"
                            value={formData.nick_name}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      )}
                      {step === "password_confirm" && (
                        <div className="w-full flex flex-col gap-3">
                          <div className="flex text-center flex-col gap-9 text-lg">
                            <p className="font-bold">
                              비밀번호를 입력해주세요.
                            </p>
                            <div className="relative w-full">
                              <input
                                ref={inputRef}
                                name="password"
                                type={showPassword ? "text" : "password"}
                                className={clsx(`bg-[#F5F5F5] text-center text-[#D9D9D9] w-full border px-4 py-3 rounded-full focus:outline-none focus:ring-0 focus:border-transparent focus:${themeClass.bg('background')} focus:text-[#0F1E6C] border-[#E8EAF9]`)}
                                placeholder="비밀번호"
                                value={formData.password}
                                onChange={handleChange}
                                required
                              />
                              <button
                                type="button"
                                className="absolute right-4 top-3 text-gray-500"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <Eye size={20} />
                                ) : (
                                  <EyeOff size={20} />
                                )}
                              </button>
                              {passwordError && (
                                <p className={clsx(themeClass.text('text'), `text-xs mt-4`)}>
                                  영문, 숫자 조합으로 8~10글자 이내
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="relative w-full mt-12">
                            <div className="flex text-center flex-col gap-9 text-lg">
                              <p className="font-bold">한번 더 입력해주세요.</p>
                              <input
                                ref={inputRef}
                                name="confirm_password"
                                type="password"
                                className={clsx(`bg-[#F5F5F5] text-center text-[#D9D9D9] w-full border px-4 py-3 rounded-full focus:outline-none focus:ring-0 focus:border-transparent focus:${themeClass.bg('background')} focus:text-[#0F1E6C] border-[#E8EAF9]`)}
                                placeholder="비밀번호 확인"
                                value={formData.confirm_password}
                                onChange={handleChange}
                                required
                              />
                            </div>
                            {confirmPasswordError && (
                              <p className="text-red-500 text-xs mt-4 text-center">
                                ! 비밀번호가 다릅니다
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {step === "phone" && (
                        <div className="flex text-center flex-col gap-9 text-lg">
                          <p className="font-bold">
                            휴대폰 번호를 입력해주세요.
                          </p>
                          <input
                            ref={inputRef}
                            name="phone"
                            className={clsx(`bg-[#F5F5F5] text-center text-[#D9D9D9] w-full border px-4 py-3 rounded-full focus:outline-none focus:ring-0 focus:border-transparent focus:${themeClass.bg('background')} focus:text-[#0F1E6C] border-[#E8EAF9]`)}
                            placeholder="전화 번호"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      )}
                      {step === "blog_url" && (
                        <div className="flex text-center flex-col gap-9 text-lg">
                          <p className="font-bold">SNS URL 또는 블로그 URL</p>
                          <input
                            ref={inputRef}
                            name="blog_url"
                            className={clsx(`bg-[#F5F5F5] text-center text-[#D9D9D9] w-full border px-4 py-3 rounded-full focus:outline-none focus:ring-0 focus:border-transparent focus:${themeClass.bg('background')} focus:text-[#0F1E6C] border-[#E8EAF9]`)}
                            placeholder="https://blog.naver.com/XXX"
                            value={formData.blog_url}
                            onChange={handleChange}
                            onBlur={(e) => validateBlogUrl(e.target.value)}
                            required
                          />
                          {blogUrlError && (
                            <p className="text-red-500 text-xs mt-2">
                              {blogUrlError}
                            </p>
                          )}
    
                        </div>
                      )}
                      {step === "terms" && (
                        <div className="flex flex-col gap-5 text-sm items-left pt-3">
                          <p className="text-lg font-bold">약관 동의</p>
                          <div className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              name="option1"
                              className="border-gray-300 peer rounded-full focus:outline-none font-bold"
                              checked={isChecked.option1}
                              onChange={handleCheck}
                            />
                            <p className="text-black font-semibold">
                              약관 전체 동의
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              name="option2"
                              className="border-gray-300 peer rounded-full focus:outline-none"
                              checked={isChecked.option2}
                              onChange={handleCheck}
                              required
                            />
                            <p className="text-black">
                              서비스 이용약관에 동의합니다. (필수)
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              name="option3"
                              className="border-gray-300 peer rounded-full focus:outline-none"
                              checked={isChecked.option3}
                              onChange={handleCheck}
                              required
                            />
                            <p className="text-black">
                              개인정보 수집 및 이용에 동의합니다. (필수)
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-xs pb-2">
                            <input
                              type="checkbox"
                              name="option4"
                              className="border-gray-300 peer rounded-full focus:outline-none"
                              checked={isChecked.option4}
                              onChange={handleCheck}
                            />
                            <p className="text-black">
                              이메일 및 SMS 수신에 동의합니다. (선택)
                            </p>
                          </div>
                          {currentStep === steps.length - 1 && (
                            <button
                              type="submit"
                              className={clsx(themeClass.bg('text'), `text-white px-4 py-2 w-full rounded-full`)}
                            >
                              회원가입하기
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </CSSTransition>
                );
              })}
              <div className="flex justify-between items-center mt-8">
                {/* {currentStep === steps.length - 1 && (
                  <button
                    type="submit"
                    className="bg-[#0F1E6C] text-white px-4 py-2 w-full rounded-full mb-10"
                  >
                    회원가입하기
                  </button>
                )} */}
                {currentStep > 0 ? (
                  <div className={clsx(themeClass.text('text'), themeClass.border('text'), 'border rounded-full w-12 h-12 flex justify-center')}>
                    <button
                      type="button"
                      className="text-sm text-[#8660B2] font-bold flex items-center justify-center text-center "
                      onClick={handleBack}
                    >
                      <svg
                        className={clsx(themeClass.text('text'), `w-6 h-6`)}
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1"
                          d="m15 19-7-7 7-7"
                        />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className=""></div>
                )}
                {currentStep >= 0 && currentStep < 6 && (
                  <div className={clsx(themeClass.text('text'), themeClass.border('text'), 'border rounded-full  w-12 h-12 flex justify-center')}>
                    <button
                      type="button"
                      className="text-sm text-[#8660B2] font-bold flex items-center justify-center text-center "
                      onClick={handleNext}
                      disabled={isEmailValidating || emailValidationError}
                    >
                      <svg
                        className={clsx(themeClass.text('text'), `w-6 h-6`)}
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1"
                          d="m9 5 7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>

          <style jsx>{`
            .input-slide-next-enter {
              opacity: 0;
              transform: translateX(50px);
            }
            .input-slide-next-enter-active {
              opacity: 1;
              transform: translateX(0);
              transition: opacity 300ms, transform 300ms;
            }
            .input-slide-next-exit {
              opacity: 1;
              transform: translateX(0);
            }
            .input-slide-next-exit-active {
              opacity: 0;
              transform: translateX(-50px);
              transition: opacity 300ms, transform 300ms;
            }
            .input-slide-back-enter {
              opacity: 0;
              transform: translateX(-50px);
            }
            .input-slide-back-enter-active {
              opacity: 1;
              transform: translateX(0);
              transition: opacity 300ms, transform 300ms;
            }
            .input-slide-back-exit {
              opacity: 1;
              transform: translateX(0);
            }
            .input-slide-back-exit-active {
              opacity: 0;
              transform: translateX(50px);
              transition: opacity 300ms, transform 300ms;
            }
          `}</style>
        </>
      )}
    </div>
  );
};

export default NewMainRegister;
